import { Box, HStack, VStack } from '@chakra-ui/react'
import type { ReactNode } from 'react'

import { Icon } from '../../components/Icons/Icon'
import { Tooltip } from '../../components/Tooltip/Tooltip'
import type { DescriptorOutput } from '../Descriptor'
import { RenderPipeline } from '../RenderPipeline'
import { analyticsCardOptions } from './AnalyticsCard.options'
import { AnalyticsCardOutputParts } from './AnalyticsCard.parts'

export { analyticsCardOptions } from './AnalyticsCard.options'
export type { AnalyticsCardOptions } from './AnalyticsCard.options'

export type AnalyticsCardInput = object

export type AnalyticsCardSlice = {
  contentPadding: number
  headerPaddingX: number
  headerPaddingY: number
}

export type AnalyticsCardOutput = {
  analyticsCard: AnalyticsCardSlice
}

const analyticsCardSetup = () =>
  RenderPipeline.createFeatureSetup<AnalyticsCardInput, AnalyticsCardOutput>(
    AnalyticsCardOutputParts.setAnalyticsCard(),
  )

export interface AnalyticsCardProps {
  renderConfig: AnalyticsCardOutput
  children?: ReactNode
}

const Card = ({ children }: AnalyticsCardProps) => (
  // GL-R-11: fill the grid cell so peer row heights match visually.
  <Box
    borderWidth="1px"
    borderColor="presets.neutral.20"
    borderRadius="4px"
    overflow="hidden"
    bg="white"
    flex="1"
    minH={0}
    minW={0}
    display="flex"
    flexDirection="column"
    alignSelf="stretch"
  >
    {children}
  </Box>
)

export interface AnalyticsCardHeaderProps {
  renderConfig: AnalyticsCardOutput & DescriptorOutput
  /** Grid drag affordance. Default false — opt in for editable dashboards. */
  showDragHandle?: boolean
  /**
   * Header action icons (table / chart / align / expand / trash).
   * Default false — Figma drilldown Expanded is title-only; opt in where needed.
   */
  showActions?: boolean
}

const Header = ({
  renderConfig,
  showDragHandle = false,
  showActions = false,
}: AnalyticsCardHeaderProps) => {
  const { analyticsCard, title, subtitle, description } = renderConfig

  return (
    <HStack
      justifyContent="space-between"
      alignItems="center"
      px={`${analyticsCard.headerPaddingX}px`}
      py={`${analyticsCard.headerPaddingY}px`}
      bg="presets.neutral.10"
      borderBottomWidth="1px"
      borderColor="presets.neutral.20"
    >
      <HStack gap="8px" alignItems="center">
        {showDragHandle ? (
          <VStack py="3px" alignItems="center">
            <Icon
              icon="AnalyticsDragIcon"
              boxSize="12px"
              color="presets.neutral.60"
            />
          </VStack>
        ) : null}
        {title != null && title !== '' ? (
          <Box
            as="span"
            fontSize="14px"
            fontWeight="600"
            color="presets.neutral.100"
            lineHeight="1.2"
          >
            {title}
          </Box>
        ) : null}
        {subtitle != null && subtitle !== '' ? (
          <Box
            as="span"
            fontSize="12px"
            fontWeight="400"
            color="presets.neutral.80"
            lineHeight="1.2"
          >
            {subtitle}
          </Box>
        ) : null}
        {description != null && description !== '' ? (
          <Tooltip label={description} placement="top">
            <VStack p="2px" aria-label={description}>
              <Icon
                icon="AnalyticsInfoIcon"
                boxSize="12px"
                color="presets.neutral.80"
              />
            </VStack>
          </Tooltip>
        ) : null}
      </HStack>
      {showActions ? (
        <HStack gap="8px" alignItems="center">
          <VStack p="2px">
            <Icon icon="TableIcon" boxSize="12px" color="presets.neutral.90" />
          </VStack>
          <VStack p="2px">
            <Icon
              icon="BarChartIcon"
              boxSize="12px"
              color="presets.neutral.90"
            />
          </VStack>
          <VStack p="2px">
            <Icon
              icon="AlignmentIcon"
              boxSize="12px"
              color="presets.neutral.90"
            />
          </VStack>
          <VStack p="2px">
            <Icon
              icon="AnalyticsExpandIcon"
              boxSize="12px"
              color="presets.neutral.90"
            />
          </VStack>
          <VStack p="2px">
            <Icon
              icon="AnalyticsTrashIcon"
              boxSize="12px"
              color="presets.neutral.90"
            />
          </VStack>
        </HStack>
      ) : null}
    </HStack>
  )
}

const Content = ({ renderConfig, children }: AnalyticsCardProps) => (
  <Box
    flex="1"
    minH={0}
    minW={0}
    display="flex"
    flexDirection="column"
    p={`${renderConfig.analyticsCard.contentPadding}px`}
  >
    {children}
  </Box>
)

export const AnalyticsCard = {
  options: analyticsCardOptions,
  setup: analyticsCardSetup,
  Card,
  Header,
  Content,
}
